import type { ComponentProps, ReactNode } from 'react';
import { sx, type StyleProp } from './style';

/**
 * `react-native-svg`'s element names over the browser's own SVG.
 *
 * react-native-svg deliberately mirrors the SVG spec, and React already accepts
 * SVG presentation attributes in camelCase, so these are near-identity wrappers
 * — they exist so the traced icon set and the court/arena diagrams port across
 * without a find-and-replace that would have to be redone every time the mobile
 * artwork changes.
 */

type SvgProps = {
  width?: number | string;
  height?: number | string;
  viewBox?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number | string;
  strokeLinecap?: 'butt' | 'round' | 'square';
  strokeLinejoin?: 'miter' | 'round' | 'bevel';
  style?: StyleProp;
  children?: ReactNode;
  preserveAspectRatio?: string;
  className?: string;
  pointerEvents?: string;
};

export default function Svg({ style, children, pointerEvents, ...rest }: SvgProps) {
  return (
    <svg
      {...rest}
      aria-hidden="true"
      focusable="false"
      style={{ ...(pointerEvents ? { pointerEvents: pointerEvents as never } : null), ...sx(style) }}
    >
      {children}
    </svg>
  );
}

export function Path(props: ComponentProps<'path'>) {
  return <path {...props} />;
}

export function Circle(props: ComponentProps<'circle'>) {
  return <circle {...props} />;
}

export function Rect(props: ComponentProps<'rect'>) {
  return <rect {...props} />;
}

export function Line(props: ComponentProps<'line'>) {
  return <line {...props} />;
}

export function G(props: ComponentProps<'g'>) {
  return <g {...props} />;
}

export function Defs(props: ComponentProps<'defs'>) {
  return <defs {...props} />;
}

export function Pattern(props: ComponentProps<'pattern'>) {
  return <pattern {...props} />;
}

export function Polygon(props: ComponentProps<'polygon'>) {
  return <polygon {...props} />;
}

export function Text(props: ComponentProps<'text'>) {
  return <text {...props} />;
}

export function ClipPath(props: ComponentProps<'clipPath'>) {
  return <clipPath {...props} />;
}

export function LinearGradient(props: ComponentProps<'linearGradient'>) {
  return <linearGradient {...props} />;
}

export function Stop(props: ComponentProps<'stop'>) {
  return <stop {...props} />;
}
